import React, { useContext, useEffect } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import UserContext from 'context/user-context'
import AdminLayout from './AdminLayout'
import MunicipalitiesList from './municipalities/MunicipalitiesList'
import MunicipalityForm from './municipalities/MunicipalityForm'
import MunicipalityDetail from './municipalities/MunicipalityDetail'
import TableColumnsList from './table-columns/TableColumnsList'
import TableColumnForm from './table-columns/TableColumnForm'
import TableColumnDetail from './table-columns/TableColumnDetail'
import AnalyticsDashboard from './analytics/AnalyticsDashboard'
import UsersList from './users/UsersList'
import UserDetail from './users/UserDetail'
import SectionsList from './sections/SectionsList'
import SectionForm from './sections/SectionForm'
import SectionDetail from './sections/SectionDetail'
import AnalyticsDataList from './analytics-data/AnalyticsDataList'
import AnalyticDataForm from './analytics-data/AnalyticDataForm'
import AnalyticDataDetail from './analytics-data/AnalyticDataDetail'
import PermissionsList from './permissions/PermissionsList'
import PermissionForm from './permissions/PermissionForm'
import PermissionDetail from './permissions/PermissionDetail'
import DataAccessesList from './data-accesses/DataAccessesList'
import DataAccessForm from './data-accesses/DataAccessForm'
import DataAccessDetail from './data-accesses/DataAccessDetail'
import IssuesList from './issues/IssuesList'
import IssueForm from './issues/IssueForm'
import IssueDetail from './issues/IssueDetail'
import SystemsList from './systems/SystemsList'
import SystemForm from './systems/SystemForm'
import SystemDetail from './systems/SystemDetail'
import MunicipalityCustomAnalytics from './custom-analytics/MunicipalityCustomAnalytics'
import StyleGuide from './style-guide/StyleGuide'
import DataPipelinesList from './data-pipelines/DataPipelinesList'

const AdminRoutes = () => {
  const currentUser = useContext(UserContext)
  const { logout } = useAuth0()
  const navigate = useNavigate()

  // Check if user is valid admin (has is_admin flag AND @thirdline.io email)
  const isValidAdmin =
    currentUser?.is_admin && currentUser?.email?.endsWith('@thirdline.io')

  useEffect(() => {
    // If user is not a valid admin, log them out
    if (!isValidAdmin) {
      console.error('Unauthorized access to admin area - logging out')
      // Clear local storage and session
      localStorage.clear()
      sessionStorage.clear()
      // Redirect to login
      logout({ returnTo: window.location.origin })
    }
  }, [isValidAdmin, logout, navigate])

  // If not valid admin, don't render any routes
  if (!isValidAdmin) {
    return null
  }

  return (
    <Routes>
      <Route path="/" element={<AdminLayout />}>
        <Route index element={<AnalyticsDashboard />} />
        <Route path="usage" element={<AnalyticsDashboard />} />

        {/* Municipality Routes */}
        <Route path="municipalities" element={<MunicipalitiesList />} />
        <Route path="municipalities/new" element={<MunicipalityForm />} />
        <Route path="municipalities/:id" element={<MunicipalityDetail />} />
        <Route path="municipalities/:id/edit" element={<MunicipalityForm />} />
        <Route
          path="municipalities/:id/custom-analytics"
          element={<MunicipalityCustomAnalytics />}
        />

        {/* User Routes */}
        <Route path="users" element={<UsersList />} />
        <Route path="users/:id" element={<UserDetail />} />

        {/* Table Column Routes */}
        <Route path="table-columns" element={<TableColumnsList />} />
        <Route path="table-columns/new" element={<TableColumnForm />} />
        <Route path="table-columns/:id" element={<TableColumnDetail />} />
        <Route path="table-columns/:id/edit" element={<TableColumnForm />} />

        {/* Section Routes */}
        <Route path="sections" element={<SectionsList />} />
        <Route path="sections/new" element={<SectionForm />} />
        <Route path="sections/:id" element={<SectionDetail />} />
        <Route path="sections/:id/edit" element={<SectionForm />} />

        {/* Analytics Data Routes */}
        <Route path="analytics-data" element={<AnalyticsDataList />} />
        <Route path="analytics-data/new" element={<AnalyticDataForm />} />
        <Route path="analytics-data/:id" element={<AnalyticDataDetail />} />
        <Route path="analytics-data/:id/edit" element={<AnalyticDataForm />} />

        {/* Permission Routes */}
        <Route path="permissions" element={<PermissionsList />} />
        <Route path="permissions/new" element={<PermissionForm />} />
        <Route path="permissions/:id" element={<PermissionDetail />} />
        <Route path="permissions/:id/edit" element={<PermissionForm />} />

        {/* Data Access Routes */}
        <Route path="data-accesses" element={<DataAccessesList />} />
        <Route path="data-accesses/new" element={<DataAccessForm />} />
        <Route path="data-accesses/:id" element={<DataAccessDetail />} />
        <Route path="data-accesses/:id/edit" element={<DataAccessForm />} />

        {/* Issue Routes */}
        <Route path="issues" element={<IssuesList />} />
        <Route path="issues/new" element={<IssueForm />} />
        <Route path="issues/:id" element={<IssueDetail />} />
        <Route path="issues/:id/edit" element={<IssueForm />} />

        {/* System Routes */}
        <Route path="systems" element={<SystemsList />} />
        <Route path="systems/new" element={<SystemForm />} />
        <Route path="systems/:id" element={<SystemDetail />} />
        <Route path="systems/:id/edit" element={<SystemForm />} />

        {/* Data Pipelines */}
        <Route path="data-pipelines" element={<DataPipelinesList />} />

        {/* Style Guide */}
        <Route path="style-guide" element={<StyleGuide />} />

      </Route>
    </Routes>
  )
}

export default AdminRoutes
